import { useDaily, useDailyEvent } from '@daily-co/daily-react';
import { useCallback, useRef, useState } from 'react';
import { VM_BROWSER_USER_NAME } from '~/common/recorder/constants';
import type { RecorderCommand } from '~/common/recorder/recorder-operation';
import { zVmServerToClientMessage } from '~/common/vm/protocol';
import { api } from '~/trpc-extra/api';
import type { RecorderClientHook, RecorderClientParams, RecorderStartArgs } from './use-recorder-client';

const RECONNECT_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
// Grace period before showing loading overlay after participant leaves
// This prevents flickering during brief disconnections
const TRACK_CLEAR_DELAY_MS = 3000; // 3 seconds

export const useRecorderClientWeb = ({
  testContentId,
  executionType,
  messageHandler,
}: RecorderClientParams): RecorderClientHook => {
  const daily = useDaily();

  const { mutateAsync: createRoom } = api.recorderWeb.createRoom.useMutation();
  const { mutateAsync: startRecorder } = api.recorderWeb.start.useMutation({
    onError(error) {
      // Ignore this, I'm not sure why it's thrown after 1.5 mins
      if (error.message.startsWith('Unexpected token')) {
      } else {
        console.error('Server error: ', error?.message ? error.message : error);
      }
    },
  });

  const [mediaStreamTrack, setMediaStreamTrack] = useState<MediaStreamTrack | undefined>();

  const reconnectTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const trackClearTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const start = useCallback(
    async ({ recorderInitiatorLocation, antiBot, useProxy, environment }: RecorderStartArgs) => {
      const newRoomName = await createRoom();

      if (!daily) {
        throw new Error('Video provider undefined');
      }

      const roomUrl = `https://stably.daily.co/${newRoomName}`;
      // This function runs throughout the life of the VM, so don't wait for it
      startRecorder({
        driverType: 'browser',
        roomName: newRoomName,
        testContentId,
        executionType,
        location: recorderInitiatorLocation,
        antiBot,
        useProxy,
        environment,
      }).catch((e) => {
        // Ignore this, I'm not sure why it's thrown after 1.5 mins
        if (e.message.startsWith('Unexpected token')) {
        } else {
          console.error('Server error: ', e);
        }
      });

      await daily.join({
        url: roomUrl,
        startAudioOff: true,
        startVideoOff: true,
        userName: 'client',
      });
    },
    [createRoom, daily, startRecorder, testContentId, executionType],
  );

  const close = useCallback(async () => {
    // Clear any pending timeouts
    if (trackClearTimeoutRef.current) {
      clearTimeout(trackClearTimeoutRef.current);
      trackClearTimeoutRef.current = undefined;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    daily?.leave().catch(() => undefined);
  }, [daily]);

  const send = useCallback(
    (command: RecorderCommand) => {
      daily?.sendAppMessage(JSON.stringify(command));
    },
    [daily],
  );

  useDailyEvent(
    'participant-left',
    useCallback(
      async (event) => {
        if (event.participant.user_name !== VM_BROWSER_USER_NAME) {
          return;
        }
        console.log('[RecorderClient] participant-left event fired for VM_BROWSER_USER_NAME');
        // Don't immediately clear the track - give the backend time to reconnect.
        // This prevents the loading overlay from appearing during brief disconnections.
        // The track will be cleared after TRACK_CLEAR_DELAY_MS if no reconnection occurs.
        if (trackClearTimeoutRef.current) {
          clearTimeout(trackClearTimeoutRef.current);
        }
        trackClearTimeoutRef.current = setTimeout(() => {
          console.log('[RecorderClient] Track clear timeout fired - clearing media track');
          setMediaStreamTrack(undefined);
          trackClearTimeoutRef.current = undefined;
        }, TRACK_CLEAR_DELAY_MS);

        // If the "host" (recorder backend) disconnects, don't close everything down
        // Instead give some time for the host to reconnect.
        // Background: Somewhat unituively this seems to trigger when switching tabs for prologned periods of time. So this code is important to keep here
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          close().catch(() => undefined);
        }, RECONNECT_TIMEOUT_MS);
      },
      [close],
    ),
  );

  // Handle participant rejoining after a disconnection
  useDailyEvent(
    'participant-joined',
    useCallback((event) => {
      if (!event) {
        return;
      }
      const { participant } = event;
      if (participant.local || participant.user_name !== VM_BROWSER_USER_NAME) {
        return;
      }
      console.log('[RecorderClient] participant-joined event fired for VM_BROWSER_USER_NAME');
      // Participant rejoined - cancel the track clear timeout
      if (trackClearTimeoutRef.current) {
        clearTimeout(trackClearTimeoutRef.current);
        trackClearTimeoutRef.current = undefined;
      }
      // Cancel the reconnect timeout since they're back
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      // The track will be set by participant-updated when it becomes playable
    }, []),
  );

  useDailyEvent(
    'participant-updated',
    useCallback((event) => {
      if (!event) {
        return;
      }
      const { participant } = event;
      if (participant.local || participant.user_name !== VM_BROWSER_USER_NAME) {
        return;
      }

      // Log track state for debugging
      const trackState = participant.tracks.video.state;
      const hasTrack = !!participant.tracks.video.persistentTrack;

      if (trackState !== 'playable' || !hasTrack) {
        console.log(
          `[RecorderClient] participant-updated: track not playable (state=${trackState}, hasTrack=${hasTrack})`,
        );
        return;
      }

      console.log('[RecorderClient] participant-updated: track is playable, setting media track');
      // Cancel any pending track clear since we have a valid track now
      if (trackClearTimeoutRef.current) {
        clearTimeout(trackClearTimeoutRef.current);
        trackClearTimeoutRef.current = undefined;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      setMediaStreamTrack(participant.tracks.video.persistentTrack);
    }, []),
  );

  useDailyEvent(
    'app-message',
    useCallback(
      (event) => {
        const message = zVmServerToClientMessage.parse(event.data);
        messageHandler({ message, close, sendServerCommand: send });
      },
      [messageHandler, close, send],
    ),
  );

  return { start, close, send, mediaSource: mediaStreamTrack ? { type: 'video', track: mediaStreamTrack } : undefined };
};
